// PostHog client (web) — `posthog-js` v1. Init в main.tsx. Autocapture off
// (только explicit events), pageview on, person_profiles: identified_only
// (анонимных персон не создаём — ТЗ §5.12).
//
// Session replay: maskAllInputs + selector маскирует user-content классы
// (.editor-content/.chat-message/.essay-content/...). Полное отключение
// replay на user-content routes — replay-guard.tsx (ТЗ §5.10).
//
// VITE_POSTHOG_ENABLED !== 'true' или нет ключа → no-op.
//
// СОГЛАСИЕ (OXEM-604). PostHog — продуктовая аналитика, и до согласия на
// категорию «аналитика» единого согласия экосистемы IEC SDK не загружается
// вовсе: ни cookie, ни localStorage, ни сети. Согласие дали — SDK грузится и
// стартует; отозвали — `opt_out_capturing()`, `reset()` и стирание его ключей
// на устройстве. Своя статистика публичных страниц (`public-traffic.ts`) — без
// cookie и агрегатами — от согласия не зависит и живёт отдельно.
// Тип — импортом ТОЛЬКО типа: он стирается сборкой. Сам SDK (252 КБ) приезжает
// динамическим импортом внутри initPostHog и потому не лежит в стартовом графе.
// Раньше статический импорт затягивал его в первый кадр даже там, где аналитика
// выключена флагом и init выходил на первой строке.
import type { PostHog } from "posthog-js";
import { getAnalyticsConsent, onConsentChange } from "@/lib/ecosystem-analytics/consent";
import { IS_CAPACITOR } from "@/lib/platform-detect";

let _enabled = false;
/** Загруженный SDK. `null`, пока init не отработал (или если он выключен). */
let ph: PostHog | null = null;
/** Идёт загрузка SDK — второй раз не начинаем. */
let loading = false;

/**
 * Экземпляр SDK для модулей, которым нужен прямой доступ (флаги, capture).
 *
 * Возвращает `null`, пока SDK не загружен (или согласия нет), — поэтому у
 * вызывающего обязана быть ветка «аналитики нет». Раньше здесь экспортировался
 * сам объект, и гарантия «он существует» держалась на статическом импорте.
 */
export function getPostHog(): PostHog | null {
	return isPostHogEnabled() ? ph : null;
}

const PII_MASK_SELECTOR =
	".editor-content, .chat-message, .essay-content, .note-content, .glossary-term, .tutor-transcript, [data-pii]";

function posthogKey(): string | null {
	const key = import.meta.env.VITE_POSTHOG_KEY ?? import.meta.env.VITE_POSTHOG_PROJECT_API_KEY;
	const enabledFlag = import.meta.env.VITE_POSTHOG_ENABLED !== "false";
	return key && enabledFlag ? key : null;
}

/**
 * Инициализация PostHog. Вызывать в main.tsx ПОСЛЕ `initConsent()`. Сам SDK
 * грузится только при согласии на аналитику и следит за его сменой.
 */
export function initPostHog(): void {
	const key = posthogKey();
	if (!key) return;
	applyConsent(key, getAnalyticsConsent() === "granted");
	onConsentChange((state) => applyConsent(key, state.analytics === "granted"));
}

function applyConsent(key: string, granted: boolean): void {
	if (granted) {
		if (ph) {
			try {
				ph.opt_in_capturing();
				_enabled = true;
			} catch {
				_enabled = false;
			}
			return;
		}
		if (loading) return;
		loading = true;
		void import("posthog-js")
			.then(({ default: loaded }) => {
				loading = false;
				// Пока грузился SDK, согласие могли отозвать.
				if (getAnalyticsConsent() !== "granted") return;
				ph = loaded;
				initLoadedPostHog(loaded, key);
			})
			.catch(() => {
				loading = false;
				_enabled = false;
			});
		return;
	}
	if (!ph) return;
	try {
		ph.opt_out_capturing();
		ph.reset();
	} catch {
		// игнор
	}
	_enabled = false;
	erasePostHogStorage();
}

/**
 * Стереть следы SDK на устройстве: `ph_*` / `__ph_*` в localStorage и cookie
 * (на хосте и на домене второго уровня — `localStorage+cookie` ставит туда).
 */
function erasePostHogStorage(): void {
	const isPostHogKey = (name: string) => name.startsWith("ph_") || name.startsWith("__ph_");
	try {
		for (const name of Object.keys(window.localStorage)) {
			if (isPostHogKey(name)) window.localStorage.removeItem(name);
		}
	} catch {
		// хранилище недоступно — стирать нечего
	}
	if (IS_CAPACITOR || typeof document === "undefined") return;
	const host = window.location.hostname;
	const parts = host.split(".");
	const domains = [host, parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${host}`];
	for (const part of document.cookie.split(";")) {
		const name = part.split("=")[0]?.trim() ?? "";
		if (!isPostHogKey(name)) continue;
		for (const domain of domains) {
			document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
		}
		document.cookie = `${name}=; Max-Age=0; path=/`;
	}
}

function initLoadedPostHog(posthog: PostHog, key: string): void {
	try {
		posthog.init(key, {
			api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com",
			autocapture: false,
			capture_pageview: true,
			capture_pageleave: true,
			// В приложении — без cookie: поддоменов там нет, а cookie WebView
			// `capacitor://localhost` не держит. На вебе `localStorage+cookie`:
			// человек остаётся тем же между поддоменами. И там и там — только
			// после согласия на аналитику (см. шапку).
			persistence: IS_CAPACITOR ? "localStorage" : "localStorage+cookie",
			person_profiles: "identified_only",
			session_recording: {
				maskAllInputs: true,
				maskTextSelector: PII_MASK_SELECTOR,
				blockSelector: ".posthog-no-record",
				recordCrossOriginIframes: false,
			},
			loaded: (ph) => {
				if (import.meta.env.DEV) ph.debug();
			},
		});
		// Прошлый отзыв мог оставить флаг opt-out в хранилище SDK — согласие
		// сейчас есть, значит сбор включён.
		if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
		_enabled = true;
	} catch {
		_enabled = false;
	}
}

/** SDK загружен и согласие на аналитику есть. */
export function isPostHogEnabled(): boolean {
	return _enabled && getAnalyticsConsent() === "granted";
}

/** Identify после login. Только UUID + low-cardinality traits (без PII). */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
	if (!isPostHogEnabled()) return;
	try {
		ph?.identify(userId, traits);
	} catch {
		// игнор
	}
}

/** Logout — обнулить distinctId. */
export function resetPostHog(): void {
	if (!ph) return;
	try {
		ph?.reset();
	} catch {
		// игнор
	}
}

/** Управление session replay по route (используется ReplayGuard). */
export function setSessionRecording(active: boolean): void {
	if (!_enabled) return;
	try {
		if (active) ph?.startSessionRecording();
		else ph?.stopSessionRecording();
	} catch {
		// игнор
	}
}
